import {
  selectActiveAccount,
  selectMemberDetails,
  selectMembersAge,
  useInsuranceRatesQuery,
  useInsuranceSpecialFieldsQuery,
  useInsurancesQuery,
} from "@/store/apis/linkApi"
import {
  ALL_GENDERS,
  GSS_PRODUCT_ID,
  GSU_PRODUCT_ID,
  WSS_PRODUCT_ID,
  SCI_POLICY_PERIOD,
  SCI_POLICY_PERIOD_DAYS,
  SCI_POLICY_PERIOD_READABLE,
  WSS_GSU_ACCOUNTS,
} from "@/utils/helpers/mappings"
import { useSelector } from "react-redux"
import { filter, find, flatten, sumBy } from "lodash"
import {
  numberWithCommas,
  roundToNearest,
} from "@/utils/helpers/numericHelpers"
import { addDays, differenceInDays, format } from "date-fns"
import {
  formatDMYtoDate,
  formatMonthlyPremium,
  formatQueryDate,
} from "@/utils/helpers/formatters"
import { usePrefixedContentJsx } from "@/utils/hooks/useContent"
import { ReactNode, useMemo } from "react"
import { ProductNumber } from "@/types/General"
import {
  Insurance,
  InsuranceBenefit,
  InsuranceProductIds,
  InsuranceRatesQuery,
  InsuranceSpecialField,
  isInsuredProductId,
  InsuranceRatesResponse,
} from "@/types/Insurance"
import { assertNotNullable } from "@/utils/helpers/utils"
import { skipToken } from "@reduxjs/toolkit/query"
import { FormikValues } from "formik"

// Content Hooks
export const useFormContent = usePrefixedContentJsx(
  "forms.change_insurance_form"
)
export const useFormSuccessContent = usePrefixedContentJsx(
  "forms.change_insurance_success_page"
)
export const useGssContent = usePrefixedContentJsx("insurance.gss")
export const useGsuWssContent = usePrefixedContentJsx("insurance.gsu_wss")
export const useGsuWssPysContent = usePrefixedContentJsx(
  "protect_your_super.gsuwss"
)

type ReadableInsuranceCombined = Record<keyof InsuranceCombined, ReactNode> & {
  totalPremium: ReactNode
  deathAndTpdPremium: ReactNode
}

function readableInsurance(data: InsuranceCombined): ReadableInsuranceCombined {
  const {
    deathCover,
    deathPremium,
    tpdCover,
    tpdPremium,
    sciCover,
    sciPremium,
  } = data
  return {
    deathPremium: deathPremium ? formatMonthlyPremium(deathPremium) : null,
    tpdPremium: tpdPremium ? formatMonthlyPremium(tpdPremium) : null,
    sciPremium: sciPremium ? formatMonthlyPremium(sciPremium) : null,
    totalPremium:
      deathPremium + tpdPremium + sciPremium
        ? formatMonthlyPremium(deathPremium + tpdPremium + sciPremium)
        : null,
    deathAndTpdPremium:
      deathPremium + tpdPremium
        ? formatMonthlyPremium(deathPremium + tpdPremium)
        : null,
    deathCover: deathCover ? `$${numberWithCommas(deathCover)}` : null,
    tpdCover: tpdCover ? `$${numberWithCommas(tpdCover)}` : null,
    sciCover: sciCover ? `$${numberWithCommas(sciCover)}` : null,
  }
}

type InsuranceCombined = {
  deathCover: number
  deathPremium: number
  tpdCover: number
  tpdPremium: number
  sciCover: number
  sciPremium: number
}

export type InsuranceCover = {
  currRaw: InsuranceCombined
  currRead: ReadableInsuranceCombined
}

// Get GSS amd WSS data
export function aggregateGssWssInsurance(
  policyBenefits: InsuranceBenefit[]
): InsuranceCover {
  const death = policyBenefits.filter(
    (policy) =>
      policy.benefitName === "Basic Death Benefit" ||
      policy.benefitName === "Voluntary Death Benefit"
  )

  const tpd = policyBenefits.filter(
    (policy) =>
      policy.benefitName === "Basic TPD Benefit" ||
      policy.benefitName === "Voluntary TPD Benefit"
  )

  const sci = policyBenefits.filter(
    (policy) =>
      policy.benefitName === "Basic SCI Benefit" ||
      policy.benefitName === "Voluntary SCI Benefit"
  )

  const currentRaw = {
    deathCover: roundToNearest(sumBy(death, "cover"), 100, "down"),
    deathPremium: sumBy(death, (insurance) =>
      sumBy(insurance.premiums, "amount")
    ),
    tpdCover: roundToNearest(sumBy(tpd, "cover"), 100, "down"),
    tpdPremium: sumBy(tpd, (insurance) => sumBy(insurance.premiums, "amount")),
    sciCover: roundToNearest(sumBy(sci, "cover"), 100, "down"),
    sciPremium: sumBy(sci, (insurance) => sumBy(insurance.premiums, "amount")),
  }

  return {
    currRaw: currentRaw,
    currRead: readableInsurance(currentRaw),
  }
}

type PolicyNumbers = {
  deathTpdPolicyNo: number | null
  sciPolicyNo: number | null
}

const extractPolicyNumbersForPost = (arr: Insurance[]): PolicyNumbers => {
  let deathTpdPolicyNo = null
  let sciPolicyNo = null
  arr.forEach((policy) => {
    const basic = policy.policyBenefits.filter(
      (policy) =>
        policy.benefitName === "Basic Death Benefit" ||
        policy.benefitName === "Basic TPD Benefit"
    )

    const voluntary = policy.policyBenefits.filter(
      (policy) =>
        policy.benefitName === "Voluntary Death Benefit" ||
        policy.benefitName === "Voluntary TPD Benefit"
    )

    const sci = policy.policyBenefits.filter(
      (policy) =>
        policy.benefitName === "Basic SCI Benefit" ||
        policy.benefitName === "Voluntary SCI Benefit"
    )

    if (basic.length || voluntary.length) {
      deathTpdPolicyNo = policy.policyId
    }

    if (sci.length) {
      sciPolicyNo = policy.policyId
    }
  })

  return {
    deathTpdPolicyNo,
    sciPolicyNo,
  }
}

type SpecialFieldsData = Omit<
  ReturnType<typeof useBasicInsuranceData>,
  "isFetching" | "isError"
>

function isSpecialFieldsApplicableId(
  id?: ProductNumber
): id is Exclude<InsuranceProductIds, typeof GSS_PRODUCT_ID> {
  return WSS_GSU_ACCOUNTS.includes(id)
}

// Collect special fields insurance data
export function useSpecialFieldsData({
  allPolicyIds,
  membersAge,
  productId,
  policyBenefits,
}: SpecialFieldsData) {
  const cleanAllPolicyIds = useMemo(
    () => allPolicyIds?.filter((id) => id !== 0),
    [allPolicyIds]
  )

  const applicableProduct =
    isSpecialFieldsApplicableId(productId) && cleanAllPolicyIds

  const {
    data: specialFields,
    isFetching,
    isError,
    isUninitialized,
  } = useInsuranceSpecialFieldsQuery(
    applicableProduct
      ? { id: productId, allPolicyIds: cleanAllPolicyIds }
      : skipToken
  )

  const computed = useMemo(() => {
    if (specialFields) {
      const flattenSpecialFields = flatten(specialFields)
      const occupations = processOccupations(
        filter(flattenSpecialFields, {
          fieldName: "Occupation Category",
        })
      )

      // Fixed insurance
      const fixedInsurance = filter(flattenSpecialFields, {
        fieldCode: "DATMAINTAGEBASE",
      }).pop()?.value?.description

      // SCI data
      const sciPremium =
        Array.isArray(policyBenefits) &&
        aggregateGssWssInsurance(policyBenefits)

      const sciWaitingPeriod = find(flattenSpecialFields, {
        fieldCode: "SCIWAITINGPER",
      })?.value?.description

      const sciEffectDate = find(
        specialFields
          .filter((item) => find(item, { fieldCode: "SCIWAITINGPER" }))
          ?.pop(),
        { fieldCode: "INSUREFFECTDATE" }
      )?.value?.description

      let sciInsurance
      let sciChangeAllowed

      if (sciPremium && sciEffectDate) {
        const sciMonthlyPremium = sciPremium.currRaw.sciPremium / 12

        sciInsurance = sciWaitingPeriod
          ? {
              sciWaitingPeriod,
              sciEffectDate,
              sciMonthlyPremium,
            }
          : null

        const isWithinStartDate =
          sciInsurance?.sciEffectDate &&
          withinPolicyPeriod(sciInsurance?.sciEffectDate)

        const policyStartDate = sciInsurance
          ? sciStartDate(sciInsurance.sciEffectDate)
          : null

        // CHECK if we need to use sciInsurance.sciWaitingPeriod overrides default
        sciChangeAllowed =
          isWithinStartDate &&
          membersAge <= 65 &&
          sciPremium.currRaw.sciCover < 4200
            ? {
                start_date: policyStartDate,
              }
            : false
      }

      return {
        occupations,
        fixedInsurance,
        sciInsurance: sciInsurance ?? null,
        sciChangeAllowed: sciChangeAllowed ?? false,
      }
    }
  }, [specialFields])

  if (!applicableProduct) {
    return {
      isFetching: false,
      isError: false,
      sciChangeAllowed: false,
      sciInsurance: null,
    }
  }

  return {
    isFetching: isFetching || isUninitialized,
    isError: isError,
    ...(computed ?? {}),
  }
}

type Occupations = {
  multiple: boolean
  deathAndTpd?: InsuranceSpecialField
  sci?: InsuranceSpecialField
}

const processOccupations = (
  occupations: InsuranceSpecialField[]
): Occupations => {
  const uniqueOcc = [
    ...new Set(
      occupations.map((occ) => {
        return occ?.value?.description
      })
    ),
  ]

  const deathAndTpdOcc = occupations
    .filter((occ) => occ?.fieldCode === "DATOCCCAT")
    .pop()

  const sciOcc = occupations
    .filter((occ) => occ?.fieldCode === "SCIOCCCAT")
    .pop()

  return {
    multiple: uniqueOcc.length > 1,
    deathAndTpd: deathAndTpdOcc,
    sci: sciOcc,
  }
}

export function useEligibleForInsurance() {
  const age = useSelector(selectMembersAge)
  return age < 70 && age > 14
}

type BasicInsuranceData = Partial<InsuranceCover> &
  Partial<PolicyNumbers> & {
    allPolicyIds: number[]
    policyBenefits: InsuranceBenefit[] | InsuranceBenefit
    hasInsurance: boolean
  }

function getCurrentInsuranceEndDate(insurances: any) {
  const today = new Date()
  let currentInsuranceEndDate = null

  if (insurances?.length) {
    insurances.forEach((i: any) => {
      const endDate = new Date(i.endDate)
      const diff = differenceInDays(endDate, today)
      if (diff > -1) currentInsuranceEndDate = endDate
    })
  }
  return currentInsuranceEndDate
}

/**
 * Hook to fetch and process basic insurance data for a specific product
 * @param id - Optional product number to fetch insurance data for. If not provided, uses the active account's product ID
 * @param skip - Optional flag to skip the API call. Defaults to false
 * @returns Object containing insurance data, loading states, and computed values
 */
export function useBasicInsuranceData(id?: ProductNumber, skip?: boolean) {
  const { productId, amount } = useSelector(selectActiveAccount)
  const account = id ?? productId

  const { data, isFetching, isError } = useInsurancesQuery(
    isInsuredProductId(account) && !skip ? account : skipToken
  )

  const membersAge = useSelector(selectMembersAge)

  const computed = useMemo<BasicInsuranceData | undefined>(() => {
    if (data) {
      const policyBenefits = data.insurances
        .map((policy) => {
          return policy.policyBenefits
        })
        .flat()

      const allPolicyIds = data.insurances.map((policy) => {
        return policy.policyId
      })

      const policyIdsForPost = extractPolicyNumbersForPost(data.insurances)

      const gssWssData =
        account !== GSS_PRODUCT_ID
          ? aggregateGssWssInsurance(policyBenefits)
          : {}

      return {
        ...gssWssData,
        allPolicyIds,
        policyBenefits:
          account === GSS_PRODUCT_ID ? policyBenefits?.[0] : policyBenefits,
        hasInsurance: !!data?.insurances.length,
        /*          hasInsurance: data?.insurances.some((insurance) => {
          const endDate = new Date(insurance.endDate)
          return endDate > new Date()
        }),*/
        currentInsuranceEndDate: getCurrentInsuranceEndDate(data?.insurances),
        ...policyIdsForPost,
      }
    }
  }, [data, account])

  return {
    isFetching,
    isError,
    membersAge,
    productId: account,
    balance: amount,
    ...(computed ?? {}),
  }
}

export const withinPolicyPeriod = (date: string) => {
  return (
    differenceInDays(new Date(), formatDMYtoDate(date)) <= SCI_POLICY_PERIOD
  )
}

export const sciStartDate = (date: string) => {
  return format(
    addDays(new Date(formatDMYtoDate(date)), SCI_POLICY_PERIOD),
    "d MMMM yyyy"
  )
}

export const useExtendedInsuranceData = () => {
  const {
    isFetching: isFetchingInsurance,
    isError: isErrorInsurance,
    ...insuranceData
  } = useBasicInsuranceData()

  const {
    isFetching: isFetchingSpecialFields,
    isError: isErrorSpecialFields,
    ...specialFieldsData
  } = useSpecialFieldsData(insuranceData)

  return {
    isFetching: isFetchingInsurance || isFetchingSpecialFields,
    isError: isErrorInsurance || isErrorSpecialFields,
    ...(insuranceData ?? {}),
    ...(specialFieldsData ?? {}),
  }
}

const changeInsuranceFormInitialValues = {
  occupationCategory: "",
  professional: {
    occupationDuties: "",
    fteRequirement: "",
    grossIncome: "",
    applicableCategories: [],
    professionalInstitute: "",
    professionalInstituteOther: "",
  },
  occupationDescription: "",

  deathCoverChange: "",

  deathDecreaseCover: {
    rawAmount: "",
    boundary: "",
    calculatedValue: "",
  },

  tpdCoverChange: "",

  tpdDecreaseCover: {
    rawAmount: "",
    boundary: "",
    calculatedValue: "",
  },

  sciCoverChange: "",

  sciIncreaseCover: {
    rawAmount: "",
    boundary: "",
    calculatedValue: "",
    acknowledgement: false,
  },
  sciDecreaseCover: {
    rawAmount: "",
    boundary: "",
    calculatedValue: "",
  },
  sciWaitingPeriod: SCI_POLICY_PERIOD_READABLE,
  fixYourInsurance: "",
  acknowledge: {
    declaration: false,
    dutyOfDisclosure: false,
    privacy: false,
  },
}

export type ChangeInsuranceFormValues = typeof changeInsuranceFormInitialValues

export const useAdjustedInitialValues = () => {
  const { sciInsurance, fixedInsurance, ...rest } = useExtendedInsuranceData()

  const replaceInitialValues = {
    sciWaitingPeriod: sciInsurance?.sciWaitingPeriod ?? "",
    fixYourInsurance: fixedInsurance ?? "",
  }

  const initialValues: ChangeInsuranceFormValues = {
    ...changeInsuranceFormInitialValues,
    ...replaceInitialValues,
  }

  return {
    initialValues,
    fixedInsurance,
    sciInsurance,
    ...rest,
  }
}

export type PysProductId = typeof GSU_PRODUCT_ID | typeof WSS_PRODUCT_ID
export const keepYourSuperFormInitialValues = {
  [GSU_PRODUCT_ID]: false,
  [WSS_PRODUCT_ID]: false,
}

export type KeepYourSuperFormValues = typeof keepYourSuperFormInitialValues

export const useKeepYourInsuranceInitialValues = () => {
  const initialValues: KeepYourSuperFormValues = keepYourSuperFormInitialValues
  return { initialValues }
}

export const periodAsNumber = (period: string) => {
  return Number(period.replace(SCI_POLICY_PERIOD_DAYS, ""))
}

export type InsuranceRates = {
  getNewRates: boolean
  newOcc: string
  newWaitingPeriod: string
  selectedNewWaitingPeriod: boolean
  values: FormikValues
}

export type InsuranceProps = FormattedData &
  InsuranceCover &
  Required<
    Pick<
      ReturnType<typeof useExtendedInsuranceData>,
      "sciInsurance" | "isFetching" | "occupations" | "sciChangeAllowed"
    >
  >

export const useInsuranceRates = ({
  newOcc,
  newWaitingPeriod,
  selectedNewWaitingPeriod,
  values,
}: InsuranceRates): InsuranceProps => {
  const {
    productId,
    membersAge,
    occupations,
    currRaw,
    currRead,
    sciInsurance,
    sciChangeAllowed,
    ...rest
  } = useExtendedInsuranceData()

  const { gender } = useSelector(selectMemberDetails)

  const nextBirthdayAge = membersAge + 1 + ""
  const genderCode = ALL_GENDERS.find((item) => item.readable === gender)?.code

  assertNotNullable(genderCode, "Invalid gender type", gender)

  // preloaded by the parent component
  assertNotNullable(currRaw)
  assertNotNullable(currRead)
  assertNotNullable(occupations)
  assertNotNullable(sciInsurance)
  assertNotNullable(sciChangeAllowed)

  const { deathCover, tpdCover, sciCover } = currRaw

  const { sci, deathAndTpd } = occupations

  // Rate queries
  // Note: Age means members age
  const deathRate = {
    age: nextBirthdayAge,
    premiumType: "DeathOnly",
    gender: genderCode,
  }

  const deathAndTpdRate = {
    age: nextBirthdayAge,
    premiumType: "DeathAndTPD",
    gender: genderCode,
  }

  const sciRate = {
    age: nextBirthdayAge,
    premiumType: "SCIBase",
    gender: genderCode,
  }

  // Occupation Category Loading queries
  // Note: Age means occupation code

  const occCatLoadDeathAndTpd = {
    age: (newOcc || deathAndTpd?.value.code) ?? "",
    premiumType: "OccLoadingDeathAndTPD",
  }

  const occCatLoadSci = {
    age: (newOcc || sci?.value.code) ?? "",
    premiumType: "OccLoadingSCI",
  }

  // Sci Waiting Period rate
  // Note: Age means waiting period
  const waitingPeriodRate = {
    age: newWaitingPeriod?.replace(SCI_POLICY_PERIOD_DAYS, ""),
    premiumType: "SCIWaitingPeriod",
  }
  const hasDeathCover = deathCover > 0
  const hasTpdCover = tpdCover > 0
  const hasSciCover = sciCover > 0
  const allQueries: InsuranceRatesQuery[] = [
    {
      key: "deathRate",
      fetch: hasDeathCover || hasTpdCover,
      params: deathRate,
    },
    {
      key: "deathAndTpdRate",
      fetch: hasTpdCover,
      params: deathAndTpdRate,
    },
    {
      key: "sciRate",
      fetch: hasSciCover,
      params: sciRate,
    },
    {
      key: "occCatLoadDeathAndTpd",
      fetch: hasDeathCover || hasTpdCover,
      params: occCatLoadDeathAndTpd,
    },
    {
      key: "occCatLoadSci",
      fetch: hasSciCover,
      params: occCatLoadSci,
    },
    {
      key: "waitingPeriodRate",
      fetch: !!sciInsurance,
      params: waitingPeriodRate,
    },
  ]

  const { data: insuranceRates, isFetching } = useInsuranceRatesQuery(
    allQueries ? { allQueries, id: productId } : skipToken
  )

  const formattedData = useMemo<FormattedData>(() => {
    if (insuranceRates) {
      return formatChangeFormData({
        rateData: insuranceRates,
        selectedNewWaitingPeriod,
        currRaw,
        ...rest,
        values,
      })
    }

    return {
      selections: values,
      newRead: {
        newTpdPremium: null,
        newDeathPremium: null,
        newTotalPremium: null,
        newDeathAndTpdPremium: null,
        newSciPremium: null,
        newDeathCover: 0,
        newSciCover: 0,
        newTpdCover: 0,
      },
      selectedNewWaitingPeriod,
      currRaw,
      ...rest,
    }
  }, [values, insuranceRates])

  return {
    ...formattedData,
    currRead,
    currRaw,
    occupations,
    sciInsurance,
    sciChangeAllowed,
    isFetching,
  }
}

export type FormattedData = ReturnType<typeof formatChangeFormData> // & InsuranceCover

export type RatesDataProps = Omit<InsuranceProps, "isFetching"> &
  Pick<InsuranceRates, "selectedNewWaitingPeriod"> &
  InsuranceCover

type FormatChangeFormData = Pick<
  InsuranceRates,
  "selectedNewWaitingPeriod" | "values"
> &
  Pick<InsuranceCover, "currRaw"> & {
    rateData: InsuranceRatesResponse
  }

const formatChangeFormData = (props: FormatChangeFormData) => {
  const {
    currRaw: {
      deathPremium: currDeathRaw,
      tpdPremium: currTpdRaw,
      sciPremium: currSciRaw,
      deathCover: currDeathCover,
      tpdCover: currTpdCover,
      sciCover: currSciCover,
    },
    selectedNewWaitingPeriod,
    rateData,
    values,
    ...rest
  } = props

  const {
    deathCoverChange,
    deathDecreaseCover,
    tpdCoverChange,
    tpdDecreaseCover,
    sciCoverChange,
    sciIncreaseCover,
    sciDecreaseCover,
    occupationCategory,
  } = values

  const {
    deathRate,
    deathAndTpdRate,
    sciRate,
    occCatLoadDeathAndTpd,
    occCatLoadSci,
    waitingPeriodRate,
  } = rateData

  const tpdRateCal = deathAndTpdRate - deathRate

  const newCover = {
    newDeathCover:
      deathCoverChange === "deathDecreaseCover"
        ? parseInt(deathDecreaseCover.calculatedValue)
        : deathCoverChange === "optOut"
          ? 0
          : // : deathCoverChange === "increaseCover"
            // ? 0
            currDeathCover,
    newTpdCover:
      tpdCoverChange === "tpdDecreaseCover"
        ? parseInt(tpdDecreaseCover.calculatedValue)
        : tpdCoverChange === "optOut"
          ? 0
          : // : tpdCoverChange === "increaseCover"
            // ? 0
            currTpdCover,
    newSciCover:
      sciCoverChange === "increaseCoverWithinRange"
        ? parseInt(sciIncreaseCover.calculatedValue)
        : sciCoverChange === "sciDecreaseCover"
          ? parseInt(sciDecreaseCover.calculatedValue)
          : sciCoverChange === "optOut"
            ? 0
            : // : sciCoverChange === "increaseCover"
              // ? 0
              currSciCover,
  }

  const perUnit = 1000

  const calculatedPremiums = {
    deathPremium:
      (newCover.newDeathCover / perUnit) * deathRate * occCatLoadDeathAndTpd, // Premium = (Sum Insured Amount/1000) x DeathOnly Rate x Occ Category Loading
    deathAndTpdPremium:
      // MIGHT BE WRONG. Not using right now
      ((newCover.newDeathCover + newCover.newTpdCover) / perUnit) *
      deathAndTpdRate *
      occCatLoadDeathAndTpd, // Premium = (Sum Insured Amount/1000) x DeathAndTPD Rate x Occ Category Loading
    tpdPremium:
      (newCover.newTpdCover / perUnit) * tpdRateCal * occCatLoadDeathAndTpd, // Premium = (Sum Insured Amount/1000) x (DeathAndTPD Rate - DeathOnly Rate) x Occ Category Loading
    sciPremium:
      (newCover.newSciCover / perUnit) *
      sciRate *
      occCatLoadSci *
      waitingPeriodRate, // Premium = (Sum Insured Amount/1000) x SCI Rate x Occ Category Loading x Waiting Period Rate
  }

  const newDeathPremium =
    occupationCategory || deathCoverChange === "deathDecreaseCover"
      ? calculatedPremiums.deathPremium
      : deathCoverChange === "optOut"
        ? 0
        : currDeathRaw

  const newTpdPremium =
    occupationCategory || tpdCoverChange === "tpdDecreaseCover"
      ? calculatedPremiums.tpdPremium
      : tpdCoverChange === "optOut"
        ? 0
        : currTpdRaw

  const newSciPremium =
    occupationCategory ||
    sciCoverChange === "increaseCoverWithinRange" ||
    sciCoverChange === "sciDecreaseCover" ||
    selectedNewWaitingPeriod
      ? calculatedPremiums.sciPremium
      : sciCoverChange === "optOut"
        ? 0
        : currSciRaw

  const newRead = {
    newDeathPremium: formatMonthlyPremium(newDeathPremium),
    newTpdPremium: formatMonthlyPremium(newTpdPremium),
    newSciPremium: formatMonthlyPremium(newSciPremium),
    newDeathAndTpdPremium: formatMonthlyPremium(
      newDeathPremium + newTpdPremium
    ),
    newTotalPremium: formatMonthlyPremium(
      newDeathPremium + newTpdPremium + newSciPremium
    ),
    ...newCover,
  }

  // KEEP THESE FOR DEBUGGING
  // console.log({ tpdRate: tpdRateCal, ...newCover, ...rateData, perUnit })

  return {
    newRead,
    selections: values,
    selectedNewWaitingPeriod,
    ...rest,
  }
}

export const useChangeInsurance = () => {
  const { productId, deathTpdPolicyNo, sciPolicyNo } =
    useExtendedInsuranceData()

  const body = {
    deathTpdPolicyNo,
    effectiveDate: formatQueryDate(new Date()), // "2022-07-20"
    sciPolicyNo,
  }

  return {
    id: productId,
    ...body,
  }
}
